<script setup lang="ts">
import { Head } from '@inertiajs/vue3'
import InternalLayout from '@/layouts/InternalLayout.vue'
</script>

<template>
  <InternalLayout title="Privacy Policy">
    <Head title="Privacy Policy">
      <meta head-key="description" name="description" content="Mow Managers privacy policy. Learn how we collect, use, and protect your personal data including address, contact information, and payment details." />
      <link rel="canonical" href="https://mowmanagers.com/privacy-policy" />
      <meta property="og:title" content="Privacy Policy - Mow Managers" />
      <meta property="og:description" content="Mow Managers privacy policy. Learn how we collect, use, and protect your personal data including address, contact information, and payment details." />
      <meta property="og:type" content="website" />
      <meta property="og:url" content="https://mowmanagers.com/privacy-policy" />
    </Head>

    <article aria-label="Privacy Policy" class="py-16 lg:py-24 bg-white">
      <div class="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <section aria-labelledby="who-we-are">
          <h2 id="who-we-are" class="text-mm-dark text-3xl font-bold">Who we are</h2>
          <p class="text-[#6d706d] text-[17px] leading-[30px] mt-4">
            Our website address is:
            <a href="https://mowmanagers.com" class="text-mm-green underline hover:text-mm-green-dark transition-colors">www.mowmanagers.com</a>.
          </p>
        </section>

        <section aria-labelledby="data-collection" class="mt-14">
          <h2 id="data-collection" class="text-mm-dark text-3xl font-bold">What personal data we collect and why we collect it</h2>

          <h3 id="media" class="text-mm-dark text-2xl font-bold mt-10">Media</h3>
          <p class="text-[#6d706d] text-[17px] leading-[30px] mt-4">
            If you upload images to the website, you should avoid uploading images with embedded location data
            (<abbr title="Exchangeable Image File Format Global Positioning System">EXIF GPS</abbr>) included.
            Visitors to the website can download and extract any location data from images on the website.
          </p>

          <h3 id="forms" class="text-mm-dark text-2xl font-bold mt-10">Forms</h3>
          <p class="text-[#6d706d] text-[17px] leading-[30px] mt-4">
            Price Quote Form: In order for us to provide you with an accurate price quote, we must collect your address
            and zip code. In addition, we will collect personal information such as your name, email address, phone number,
            and a gate code if you have a lock on your gate. Mow Managers also collects your credit card data to process
            payments.
          </p>

          <h3 id="cookies" class="text-mm-dark text-2xl font-bold mt-10">Cookies</h3>
          <p class="text-[#6d706d] text-[17px] leading-[30px] mt-4">
            If you visit the My Account page, we will set a temporary cookie to determine if your browser accepts cookies.
            This cookie contains no personal data and is discarded when you close your browser.
          </p>
          <p class="text-[#6d706d] text-[17px] leading-[30px] mt-4">
            When you log in, we will also set up several cookies to save your login information and your screen display
            choices. Login cookies last for two days, and screen options cookies last for a year. If you select "Remember
            Me", your login will persist for two weeks. If you log out of your account, the login cookies will be removed.
          </p>

          <h3 id="analytics" class="text-mm-dark text-2xl font-bold mt-10">Analytics</h3>
          <p class="text-[#6d706d] text-[17px] leading-[30px] mt-4">
            We use third-party analytics services to help understand website usage. These tools may collect information
            sent by your browser as part of a web page request, such as cookies or your IP address.
          </p>
        </section>

        <section aria-labelledby="data-sharing" class="mt-14">
          <h2 id="data-sharing" class="text-mm-dark text-3xl font-bold">Who we share your data with</h2>
          <p class="text-[#6d706d] text-[17px] leading-[30px] mt-4">
            If you leave a comment, the comment and its metadata are retained indefinitely. This is so we can recognize and
            approve any follow-up comments automatically instead of holding them in a moderation queue.
          </p>
          <p class="text-[#6d706d] text-[17px] leading-[30px] mt-4">
            Third party company PayTrace is provided your credit card information so it can process it, other than that
            they only receive your payment details to process a transaction, like your zip code.
          </p>
        </section>

        <section aria-labelledby="data-retention" class="mt-14">
          <h2 id="data-retention" class="text-mm-dark text-3xl font-bold">How long we retain your data</h2>
          <p class="text-[#6d706d] text-[17px] leading-[30px] mt-4">
            If you leave a comment, the comment and its metadata are retained indefinitely. This is so we can recognize and
            approve any follow-up comments automatically instead of holding them in a moderation queue.
          </p>
          <p class="text-[#6d706d] text-[17px] leading-[30px] mt-4">
            For users that register on our website (if any), we also store the personal information they provide in their
            user profile. All users can see, edit, or delete their personal information at any time (except they cannot
            change their username). Website administrators can also see and edit that information.
          </p>
        </section>

        <section aria-labelledby="data-rights" class="mt-14">
          <h2 id="data-rights" class="text-mm-dark text-3xl font-bold">What rights you have over your data</h2>
          <p class="text-[#6d706d] text-[17px] leading-[30px] mt-4">
            If you have an account on this site, or have left comments, you can request to receive an exported file of the
            personal data we hold about you, including any data you have provided to us. You can also request that we erase
            any personal data we hold about you. This does not include any data we are obliged to keep for administrative,
            legal, or security purposes.
          </p>
        </section>

        <section aria-labelledby="data-destination" class="mt-14">
          <h2 id="data-destination" class="text-mm-dark text-3xl font-bold">Where we send your data</h2>
          <p class="text-[#6d706d] text-[17px] leading-[30px] mt-4">
            The website stores all information entered in to the website, except all of your credit card details. Only the
            last four digits of the user's credit card information is stored to help identify a user's card with their
            account. This is used in an instance where a customer needs to update their payment information. All
            information is transferred encrypted via an
            <abbr title="Secure Sockets Layer">SSL</abbr> from the website to all outside sources.
            Third party company PayTrace is provided your credit card information so it can process it, other than that
            they only receive your payment details to process a transaction, like your zip code. All other details like
            your address and personal information is stored by Mow Managers in databases for current and future uses.
          </p>
        </section>

        <section aria-labelledby="contact-info" class="mt-14">
          <h2 id="contact-info" class="text-mm-dark text-3xl font-bold">Contact information</h2>
          <address class="text-[#6d706d] text-[17px] leading-[30px] mt-4 not-italic">
            <span class="font-semibold text-mm-dark">Mow Managers</span><br />
            <a href="tel:+18178863411" class="text-mm-green underline hover:text-mm-green-dark transition-colors">817-886-3411</a>
          </address>
        </section>

        <section aria-labelledby="additional-info" class="mt-14">
          <h2 id="additional-info" class="text-mm-dark text-3xl font-bold">Additional information</h2>

          <h3 id="data-protection" class="text-mm-dark text-2xl font-bold mt-10">How we protect your data</h3>
          <p class="text-[#6d706d] text-[17px] leading-[30px] mt-4">
            All information is transferred encrypted via an
            <abbr title="Secure Sockets Layer">SSL</abbr> from the website to all outside sources.
            Only the last four digits of the user's credit card information is stored to help identify a user's card
            with their account.
          </p>

          <h3 id="data-breach" class="text-mm-dark text-2xl font-bold mt-10">What data breach procedures we have in place</h3>
          <p class="text-[#6d706d] text-[17px] leading-[30px] mt-4">
            For information regarding our data breach procedures, contact
            <a href="mailto:joe@baxterit.com" class="text-mm-green underline hover:text-mm-green-dark transition-colors">joe@baxterit.com</a>.
          </p>

          <h3 id="third-party-data" class="text-mm-dark text-2xl font-bold mt-10">What third parties we receive data from</h3>
          <p class="text-[#6d706d] text-[17px] leading-[30px] mt-4">
            Targeting demographics are purchased and used for marketing from Google and Facebook. Email
            <a href="mailto:info@mowmanagers.com" class="text-mm-green underline hover:text-mm-green-dark transition-colors">info@mowmanagers.com</a>
            for an updated list of sources. Text messaging data is processed through Podium Corp. Inc.
          </p>

          <h3 id="automated-decisions" class="text-mm-dark text-2xl font-bold mt-10">What automated decision making and/or profiling we do with user data</h3>
          <p class="text-[#6d706d] text-[17px] leading-[30px] mt-4">
            We have no automated decision making or profiling.
          </p>

          <h3 id="regulatory" class="text-mm-dark text-2xl font-bold mt-10">Industry regulatory disclosure requirements</h3>
          <p class="text-[#6d706d] text-[17px] leading-[30px] mt-4">
            We have no industry regulatory disclosures.
          </p>
        </section>
      </div>
    </article>
  </InternalLayout>
</template>
